import { alpha, styled } from '@mui/material/styles';
import {
  keyframes,
  Paper,
  Box,
  ToggleButtonGroup,
  Card,
  IconButton,
  Button,
  Grid,
  Link,
  Typography as MuiTypography,
  Divider,
  ListItem,
  ListItemButton,
  Stepper,
  Avatar,
  TextField,
  ListItemText,
  Select,
  MenuItem,
} from '@mui/material';
import StepConnector, { stepConnectorClasses } from '@mui/material/StepConnector';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CloseIcon from '@mui/icons-material/Close';
import { NavLink } from 'react-router-dom';

import { responsiveBreakpoints } from './theme';

export const StyledItem = styled(Paper)(({ theme }) => ({
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: 'center',
  backgroundColor: theme.palette.background.common,
}));

export const StyledBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
}));

export const StyledSupportBox = styled('div')({
  padding: '20px',
});

export const StyledDividerBox = styled(Box)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

export const SupportStyledItem = styled('div')(({ theme }) => ({
  border: `1px solid ${theme.palette.common.greyBorder}`,
  borderRadius: '20px',
  padding: theme.spacing(3),
  textAlign: 'center',
  color: theme.palette.text.secondary,
  backgroundColor: theme.palette.background.common,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '480px',
  maxWidth: '480px',
  margin: 'auto',
  [theme.breakpoints.down('sm')]: {
    width: '320px',
    maxWidth: '320px',
  },
}));

export const StyledCenteredBox = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

export const StyledOSIcons = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  gap: '32px',
  marginBottom: '18px',
  marginTop: '26px',
  color: theme.palette.text.primary,
  [theme.breakpoints.down('sm')]: {
    gap: '18px',
  },
}));

export const StyledVersionsBox = styled(Grid)(() => ({
  flexGrow: 1,
  minWidth: '200px',
  maxWidth: '760px',
  marginLeft: '10px',
  marginTop: '10px',
}));

//urlChecker

export const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

export const RotatingIcon = styled(AutorenewIcon)(({ theme }) => ({
  animation: `${rotate} 2s linear infinite`,
  color: theme.palette.info.main,
}));

export const StyledIcon = styled('div')(({ hoverColor }) => ({
  fontSize: '35px',
  transition: 'color 0.3s',
  cursor: 'pointer',
  '&:hover': {
    color: hoverColor,
  },
}));

export const StyledIconButton = styled(IconButton)(({ theme }) => ({
  marginRight: theme.spacing(2),
  color: theme.palette.primary.main,
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
  width: '50px',
  height: '40px',
  borderRadius: '5px',
  border: `1px solid ${theme.palette.primary.main}`,
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

export const StyledButton = styled(Button)(({ theme }) => ({
  color: theme.palette.common.white,
}));

export const StyledLink = styled(Link)(({ theme }) => ({
  '&:hover': {
    color: theme.palette.primary.main,
  },
}));

export const StyledListItem = styled(ListItem)(() => ({
  root: {
    '&:hover': {
      color: 'white',
    },
  },
}));

export const StyledListItemButton = styled(ListItemButton)(() => ({
  root: {
    '&:hover': {
      color: 'white',
    },
  },
}));

export const StyledCloseIcon = styled(CloseIcon)(({ theme }) => ({
  fontSize: 24,
  overflow: 'visible',
  cursor: 'pointer',
  color: theme.palette.main,
  transition: 'color 0.3s ease, transform 0.3s ease',
  '&:hover': {
    color: theme.palette.error.main,
    transform: 'scale(1.1)',
  },
}));

export const StyledToggleButtonGroup = styled(ToggleButtonGroup, {
  shouldForwardProp: (prop) => prop !== 'hasError',
})(({ theme, hasError }) => ({
  gap: theme.spacing(0.5),
  '& .MuiToggleButtonGroup-grouped': {
    border: `1px solid ${hasError ? theme.palette.error.main : theme.palette.divider}`,
    boxShadow: 'none',
    color: hasError ? theme.palette.error.main : theme.palette.text.secondary,
    '&:not(:first-of-type)': {
      borderRadius: theme.shape.borderRadius,
      borderLeft: `1px solid ${hasError ? theme.palette.error.main : theme.palette.divider}`,
    },
    '&:first-of-type': {
      borderRadius: theme.shape.borderRadius,
    },
    '&.Mui-selected, &.Mui-selected:hover': {
      color: 'white',
      backgroundColor: theme.palette.primary.main,
      borderColor: theme.palette.primary.main,
    },
    '&:hover': {
      borderColor: hasError ? theme.palette.error.main : theme.palette.divider,
    },
  },
}));

export const StyledTestSessionBox = styled(Box)(({ theme }) => ({
  position: 'fixed',
  inset: 0,
  width: '100%',
  maxWidth: '100vw',
  height: '100vh',
  maxHeight: '100vh',
  minHeight: 0,
  border: '2px #000',
  borderRadius: 2,
  backgroundColor: theme.palette.background.common,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  boxSizing: 'border-box',
  overscrollBehavior: 'contain',
  touchAction: 'manipulation',
  paddingTop: 'env(safe-area-inset-top)',
  paddingRight: 'env(safe-area-inset-right)',
  paddingBottom: 'env(safe-area-inset-bottom)',
  paddingLeft: 'env(safe-area-inset-left)',

  '@supports (height: 100dvh)': {
    height: '100dvh',
    maxHeight: '100dvh',
  },

  '@supports (width: 100dvw)': {
    maxWidth: '100dvw',
  },

  [theme.breakpoints.down('sm')]: {
    paddingTop: `max(${theme.spacing(1)}, env(safe-area-inset-top))`,
    paddingRight: `max(${theme.spacing(1)}, env(safe-area-inset-right))`,
    paddingBottom: `max(${theme.spacing(1)}, env(safe-area-inset-bottom))`,
    paddingLeft: `max(${theme.spacing(1)}, env(safe-area-inset-left))`,
  },

  [theme.breakpoints.down(480)]: {
    paddingTop: `max(${theme.spacing(0.5)}, env(safe-area-inset-top))`,
    paddingRight: `max(${theme.spacing(0.5)}, env(safe-area-inset-right))`,
    paddingBottom: `max(${theme.spacing(0.5)}, env(safe-area-inset-bottom))`,
    paddingLeft: `max(${theme.spacing(0.5)}, env(safe-area-inset-left))`,
    borderRadius: 0,
  },
}));

export const StyledContentBox = styled(Box)(() => ({
  flex: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
  WebkitOverflowScrolling: 'touch',
  overscrollBehavior: 'contain',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  width: '100%',
  marginLeft: 'auto',
  marginRight: 'auto',
  justifyContent: 'flex-start',
}));

export const StyledSessionContentWrapper = styled(Box)(({ theme }) => ({
  width: '100%',
  flex: 1,
  backgroundColor: 'inherit',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  minHeight: 0,
  overflowY: 'auto',
  WebkitOverflowScrolling: 'touch',
  overscrollBehavior: 'contain',

  [theme.breakpoints.up('lg')]: {
    justifyContent: 'safe center',
  },
}));

export const StyledSessionContentPanel = styled(Box)(({ theme }) => ({
  boxSizing: 'border-box',
  width: '100%',
  backgroundColor: theme.palette.background.common,
  marginLeft: 'auto',
  marginRight: 'auto',
  padding: theme.spacing(1.5),

  '@media (orientation: landscape) and (max-width: 1199.95px)': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    flex: 1,
    minHeight: 0,
    padding: theme.spacing(1),
  },

  [theme.breakpoints.up('lg')]: {
    padding: theme.spacing(1.5),
  },

  '@media (min-width: 1200px) and (max-width: 1919.95px) and (max-height: 1000px)': {
    paddingTop: theme.spacing(0.75),
    paddingBottom: theme.spacing(0.75),
  },
}));

export const StyledSessionInnerLayout = styled(Box, {
  shouldForwardProp: (prop) => !['isMultifocal', 'isLandscapeLayout'].includes(prop),
})(({ theme, isMultifocal, isLandscapeLayout }) => ({
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  maxHeight: 'none',
  height: 'auto',
  flexGrow: 0,
  width: '100%',
  marginLeft: 'auto',
  marginRight: 'auto',
  gap: theme.spacing(2),
  alignItems: 'stretch',
  paddingBottom: theme.spacing(1),

  ...(isLandscapeLayout && {
    display: 'grid',
    gridTemplateColumns: isMultifocal
      ? 'minmax(0, 1.45fr) minmax(0, 1fr)'
      : 'minmax(0, 1.25fr) minmax(0, 1fr)',
    height: 'auto',
    minHeight: 'max-content',
    flex: '0 0 auto',
    gap: theme.spacing(1.5),
    alignItems: 'stretch',
    paddingBottom: 0,
  }),

  [theme.breakpoints.up('lg')]: {
    display: 'grid',
    gridTemplateColumns: isMultifocal
      ? 'minmax(0, 1.5fr) minmax(0, 0.8fr)'
      : 'minmax(0, 1.45fr) minmax(0, 1fr)',
    width: '97%',
    minHeight: 0,
    gap: theme.spacing(4),
    alignItems: 'stretch',
  },
}));

const sessionColumnCard = (theme) => ({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'stretch',
  gap: theme.spacing(0),
  height: 'auto',
  minHeight: 0,
  padding: theme.spacing(1),
  boxSizing: 'border-box',
  borderRadius: theme.shape.borderRadius * 2.5,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.common,
  boxShadow:
    theme.palette.mode === 'dark'
      ? '0 1px 3px rgba(0, 0, 0, 0.4)'
      : '0 1px 2px rgba(16, 24, 40, 0.06), 0 6px 16px rgba(16, 24, 40, 0.05)',
});

export const StyledSessionLeftColumn = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isLandscapeLayout',
})(({ theme, isLandscapeLayout }) => ({
  ...sessionColumnCard(theme),
  ...(isLandscapeLayout && {
    alignSelf: 'stretch',
    height: 'auto',
    minHeight: 'max-content',
    overflow: 'visible',
    '& > :not(.MuiToggleButtonGroup-root)': {
      minHeight: 0,
    },
  }),
}));

export const StyledSessionCameraViewport = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isLandscapeLayout',
})(({ isLandscapeLayout }) => ({
  display: 'flex',
  width: '100%',
  minHeight: 0,
  alignItems: 'center',
  justifyContent: 'center',
  ...(isLandscapeLayout && {
    flex: '0 0 auto',
    height: 'auto',
    overflow: 'hidden',
    '& > *, & > * > *': {
      minHeight: 0,
      height: 'auto',
      maxHeight: '100%',
    },
  }),
}));

export const StyledSessionRightColumn = styled(Box, {
  shouldForwardProp: (prop) =>
    !['isMultifocal', 'isLandscapeLayout', 'hasVisualization'].includes(prop),
})(({ theme, isMultifocal, isLandscapeLayout, hasVisualization }) => ({
  ...sessionColumnCard(theme),
  flex: isMultifocal ? 0.8 : 1,
  ...(isLandscapeLayout && {
    alignSelf: hasVisualization ? 'stretch' : 'start',
    height: 'auto',
    minHeight: hasVisualization ? 'max-content' : 0,
    overflow: 'visible',
  }),
}));

export const StyledNotesAndAlertBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  marginTop: theme.spacing(1),
  width: '100%',
  flex: '0 0 auto',
  minHeight: 0,
  gap: theme.spacing(1),
  boxSizing: 'border-box',

  '& > *': {
    flex: '0 0 auto',
    minHeight: 0,
    minWidth: 0,
  },

  '& .MuiFormControl-root': {
    width: '100%',
  },

  '& .MuiInputBase-root': {
    alignItems: 'stretch',
  },

  '& textarea': {
    overflow: 'auto !important',
  },

  [theme.breakpoints.up('xl')]: {
    gap: theme.spacing(2),
  },

  '@media (min-width: 1200px) and (min-height: 800px)': {
    flex: '1 1 auto',

    '& > :last-child': {
      flex: '1 1 auto',
      height: '100%',
    },

    '& .MuiFormControl-root': {
      height: '100%',
    },

    '& .MuiInputBase-root': {
      minHeight: 96,
      flex: '1 1 auto',
    },
  },

  '@media (min-width: 1536px) and (min-height: 900px)': {
    '& .MuiInputBase-root': {
      minHeight: 112,
    },
  },
}));

export const StyledNotesWrapper = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(1.2),
  width: '100%',
  boxSizing: 'border-box',

  '@media (min-width: 1200px) and (min-height: 800px)': {
    flex: '1 1 auto',

    '& > *': {
      height: '100%',
    },

    '& .MuiFormControl-root': {
      height: '100%',
    },

    '& .MuiInputBase-root': {
      minHeight: 96,
      flex: '1 1 auto',
    },
  },

  '@media (min-width: 1536px) and (min-height: 900px)': {
    '& .MuiInputBase-root': {
      minHeight: 112,
    },
  },
}));

export const StyledVFMapWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  flex: '1.2 1 0',
  minHeight: 0,
  width: '100%',
  marginTop: theme.spacing(0.75),
  gap: theme.spacing(0.75),

  [theme.breakpoints.up('xl')]: {
    gap: theme.spacing(2),
  },

  '@media (orientation: landscape) and (max-width: 1199.95px)': {
    flex: '1 1 auto',
    justifyContent: 'center',
    marginTop: theme.spacing(0.5),
    gap: theme.spacing(0.25),
  },
}));

export const StyledProgressBarWrapper = styled(Box, {
  shouldForwardProp: (prop) => !['isSmallOrSmaller', 'useInlineLayout'].includes(prop),
})(({ theme, isSmallOrSmaller, useInlineLayout }) => {
  const useInlinePosition = isSmallOrSmaller || useInlineLayout;

  return {
    position: useInlinePosition ? 'relative' : 'absolute',
    top: 'auto',
    bottom: useInlinePosition ? 'auto' : theme.spacing(1.75),
    left: useInlinePosition ? 'auto' : '50%',
    transform: useInlinePosition ? 'none' : 'translateX(-50%)',
    width: useInlinePosition ? '100%' : 'clamp(360px, 42vw, 520px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1400,
    pointerEvents: 'none',
    marginTop: useInlinePosition ? theme.spacing(0.5) : 0,
    marginBottom: useInlinePosition ? theme.spacing(0.5) : 0,

    [theme.breakpoints.up('sm')]: {
      width: useInlinePosition ? '100%' : 'clamp(360px, 42vw, 520px)',
    },
    [theme.breakpoints.up('md')]: {
      width: useInlinePosition ? '100%' : 'clamp(380px, 42vw, 520px)',
    },
    [theme.breakpoints.up('lg')]: {
      width: useInlinePosition ? '100%' : 'clamp(420px, 36vw, 540px)',
    },
  };
});

export const StyledVFMapContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isMultifocal',
})(({ theme, isMultifocal }) => ({
  flex: '0 1 auto',
  width: '100%',
  maxWidth: isMultifocal ? 'min(680px, 68vh)' : 'min(420px, 38vh)',
  maxHeight: '100%',
  aspectRatio: isMultifocal ? '1.8' : '1.35',
  height: 'auto',
  minHeight: 0,
  overflow: 'hidden',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  margin: '0 auto',

  [theme.breakpoints.up('xl')]: {
    maxWidth: isMultifocal ? 'min(760px, 68vh)' : 'min(460px, 38vh)',
  },

  [theme.breakpoints.up('xxl')]: {
    maxWidth: isMultifocal ? 'min(820px, 68vh)' : 'min(520px, 38vh)',
  },

  '@supports (height: 100dvh)': {
    maxWidth: isMultifocal ? 'min(680px, 68dvh)' : 'min(420px, 38dvh)',

    [theme.breakpoints.up('xl')]: {
      maxWidth: isMultifocal ? 'min(760px, 68dvh)' : 'min(460px, 38dvh)',
    },

    [theme.breakpoints.up('xxl')]: {
      maxWidth: isMultifocal ? 'min(820px, 68dvh)' : 'min(520px, 38dvh)',
    },
  },

  '@media (orientation: landscape) and (max-width: 1199.95px)': {
    width: 'min(100%, 290px)',
    maxWidth: isMultifocal ? 'min(350px, 42vh)' : 'min(290px, 38vh)',
  },
}));

// Corner radius of the video cards; the top bars use it for their top corners.
const VIDEO_CARD_RADIUS = 6;

const VIDEO_TOP_BAR_HEIGHT = { xs: 28, md: 32, lg: 32, xl: 36 };
// Bars shrink with the window (1.875vw = 36px on a 1920px-wide window), so browser
// zoom doesn't make them taller relative to the video; capped at the heights above
// and never below 20px so the icons fit.
const VIDEO_TOP_BAR_MIN_HEIGHT = 20;
const VIDEO_TOP_BAR_VW = 1.875;
// Text scales the same way (15px on a 1920px-wide window, never below 9px), so
// both bars' text shrinks together when zooming instead of only the scene's.
const VIDEO_TOP_BAR_TEXT_VW = 0.78125;
const videoTopBarFontSize = (maxPx) => `clamp(9px, ${VIDEO_TOP_BAR_TEXT_VW}vw, ${maxPx}px)`;
const videoTopBarHeight = (maxPx) =>
  `clamp(${VIDEO_TOP_BAR_MIN_HEIGHT}px, ${VIDEO_TOP_BAR_VW}vw, ${maxPx}px)`;

// Bar height and text size per breakpoint, passed to toStyle(height, fontSize).
// Only mouse devices (pointer: fine) get the window scaling above: that's where
// browser zoom happens. Touch devices (e.g. an iPad mini) keep the full sizes,
// since their narrow viewport is a small screen, not zoom.
const videoTopBarSizes = (theme, toStyle) => {
  const sizes = (scaled, heightPx, fontPx) =>
    scaled
      ? toStyle(videoTopBarHeight(heightPx), videoTopBarFontSize(fontPx))
      : toStyle(`${heightPx}px`, `${fontPx}px`);
  // Text matches the pre-test checklist (InstructionsText): 13px, 15px from xl.
  const perBreakpoint = (scaled) => ({
    ...sizes(scaled, VIDEO_TOP_BAR_HEIGHT.xs, 13),
    [theme.breakpoints.up('md')]: sizes(scaled, VIDEO_TOP_BAR_HEIGHT.md, 13),
    [theme.breakpoints.up('lg')]: sizes(scaled, VIDEO_TOP_BAR_HEIGHT.lg, 13),
    [theme.breakpoints.up('xl')]: sizes(scaled, VIDEO_TOP_BAR_HEIGHT.xl, 15),
  });
  return { ...perBreakpoint(false), '@media (pointer: fine)': perBreakpoint(true) };
};

export const StyledLiveStreamWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isFullscreen',
})(({ theme, isFullscreen }) => ({
  display: 'flex',
  flex: isFullscreen ? 'none' : '0 0 auto',
  minHeight: 0,
  width: '100%',
  alignItems: 'center',
  justifyContent: 'center',
  containerType: isFullscreen ? 'normal' : 'inline-size',

  // Desktop two-column layout: claim the column's available height so the
  // aspect-sized video box centers within it.
  [theme.breakpoints.up('lg')]: {
    flex: isFullscreen ? 'none' : '1 1 auto',
  },
}));

// Colours for the video top bars, set on StyledLiveStreamBox. Styled like the
// home page status banners (tinted background, stronger border), in a neutral
// colour: the theme's text colour on the page, white on black in fullscreen.
const videoTopBarColorVars = (theme, isFullscreen) => {
  const onDark = isFullscreen || theme.palette.mode === 'dark';
  return {
    '--video-bar-fg': isFullscreen ? theme.palette.common.white : theme.palette.text.primary,
    '--video-bar-tint': onDark ? '7.5%' : '4.5%',
    '--video-bar-border': onDark ? '22%' : '14%',
    '--video-bar-active': onDark ? theme.palette.primary.light : theme.palette.primary.main,
  };
};

// Same cap StyledLiveStreamBox applies in the compact landscape layout.
const COMPACT_LANDSCAPE_MEDIA = '@media (orientation: landscape) and (max-width: 1199.95px)';
const COMPACT_LANDSCAPE_MAX_HEIGHT = '72vh, calc(100vh - 150px)';

// Under '&&' so parent rules can't override them: the video fills its area
// absolutely and adds no height, so losing minHeight (e.g. to the compact
// landscape viewport's `& > * > *` reset) would collapse the card to the bar.
const fillCardHeights = (theme, videoAspect) => {
  const videoHeight = `100cqw / (${videoAspect})`;
  return {
    '&&': {
      alignSelf: 'stretch',
      height: 'auto',
      minHeight: `calc(${videoHeight})`,
      ...videoTopBarSizes(theme, (barHeight) => ({
        maxHeight: `min(100%, calc(${videoHeight} + ${barHeight}))`,
      })),
      [COMPACT_LANDSCAPE_MEDIA]: {
        minHeight: `min(calc(${videoHeight}), ${COMPACT_LANDSCAPE_MAX_HEIGHT})`,
        maxHeight: `min(100%, calc(${videoHeight} + ${VIDEO_TOP_BAR_HEIGHT.md}px), ${COMPACT_LANDSCAPE_MAX_HEIGHT})`,
      },
    },
  };
};

export const StyledLiveStreamBox = styled(Box, {
  shouldForwardProp: (prop) => !['isFullscreen', 'videoAspect', 'hasTopBar'].includes(prop),
})(({ theme, isFullscreen, videoAspect, hasTopBar }) => ({
  flex: isFullscreen ? 'none' : '0 1 auto',
  minHeight: 0,
  height: isFullscreen ? '100vh' : 'auto',
  maxHeight: '100%',
  ...(!isFullscreen &&
    videoAspect &&
    (hasTopBar ? fillCardHeights(theme, videoAspect) : { aspectRatio: videoAspect })),
  position: isFullscreen ? 'fixed' : 'relative',
  inset: isFullscreen ? 0 : 'auto',
  display: 'flex',
  flexDirection: 'column',
  flexWrap: 'nowrap',
  justifyContent: 'flex-start',
  alignItems: 'stretch',
  overflow: 'hidden',
  backgroundColor: isFullscreen ? '#000' : 'transparent',
  ...videoTopBarColorVars(theme, isFullscreen),
  borderRadius: isFullscreen ? 0 : VIDEO_CARD_RADIUS,
  overflowY: 'hidden',
  width: '100%',
  maxWidth: isFullscreen ? '100%' : 'none',
  boxSizing: 'border-box',
  overscrollBehavior: 'contain',
  touchAction: isFullscreen ? 'none' : 'manipulation',

  ...(isFullscreen
    ? {
        zIndex: 1500,
        '@supports (height: 100dvh)': {
          height: '100dvh',
          maxHeight: '100dvh',
        },
      }
    : {
        '@media (min-width: 900px)': {
          width: '100%',
          marginLeft: 'auto',
          marginRight: 'auto',
        },
        '@media (orientation: landscape) and (max-width: 1199.95px)': {
          width: '100%',
          height: 'auto',
          maxHeight: 'min(72vh, calc(100vh - 150px))',
          marginLeft: 'auto',
          marginRight: 'auto',

          '@supports (height: 100dvh)': {
            maxHeight: 'min(72dvh, calc(100dvh - 150px))',
          },
        },
      }),
}));

export const videoTopBarBase = (theme) => ({
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  width: '100%',
  boxSizing: 'border-box',
  color: 'var(--video-bar-fg)',
  fontFamily: theme.typography.fontFamily,
  fontWeight: 500,
  lineHeight: 1.2,
  letterSpacing: 0.5,
  whiteSpace: 'nowrap',
  // Children inherit the font size.
  ...videoTopBarSizes(theme, (height, fontSize) => ({ height, fontSize })),
  // Short landscape screens, like the pre-test checklist.
  '@media (orientation: landscape) and (max-width: 1199.95px) and (max-height: 699px)': {
    fontSize: 11,
  },
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  backgroundColor: 'color-mix(in srgb, var(--video-bar-fg) var(--video-bar-tint), transparent)',
  border: '1px solid color-mix(in srgb, var(--video-bar-fg) var(--video-bar-border), transparent)',
  borderRadius: `${VIDEO_CARD_RADIUS}px ${VIDEO_CARD_RADIUS}px 0 0`,
  pointerEvents: 'none',
  userSelect: 'none',
});

export const StyledVideoArea = styled(Box, {
  shouldForwardProp: (prop) => !['isFullscreen', 'aspect'].includes(prop),
})(({ isFullscreen, aspect }) => ({
  position: 'relative',
  width: '100%',
  minHeight: 0,
  flex: !isFullscreen && aspect ? '0 1 auto' : '1 1 auto',
  aspectRatio: !isFullscreen && aspect ? aspect : 'auto',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  backgroundColor: '#000',
}));

export const StyledEyeIndicatorBar = styled(Box)(({ theme }) => ({
  ...videoTopBarBase(theme),
  justifyContent: 'space-around',
  gap: theme.spacing(2),
  cursor: 'default',
}));

export const StyledEyeIndicatorItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active',
})(({ theme, active }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  // In em like the scene rows, so icon and text spacing scale together.
  gap: '0.46em',
  minWidth: 56,
  justifyContent: 'center',
  color: active
    ? 'var(--video-bar-active)'
    : 'color-mix(in srgb, var(--video-bar-fg) 62%, transparent)',
  transition: theme.transitions.create(['color', 'opacity']),
  '& svg': {
    fontSize: '1.15em',
  },
}));

export const StyledFullscreenButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'isFullscreen',
})(({ theme, isFullscreen }) => ({
  position: isFullscreen ? 'fixed' : 'absolute',
  top: '10px',
  right: isFullscreen ? 'max(12px, env(safe-area-inset-right))' : '10px',
  minWidth: 44,
  minHeight: 44,
  backgroundColor: 'rgba(0, 0, 0, 0.42)',
  color: '#fff',
  zIndex: isFullscreen ? 1600 : 2,
  pointerEvents: 'auto',
  touchAction: 'manipulation',
  WebkitTapHighlightColor: 'transparent',
  WebkitTransform: isFullscreen ? 'translateZ(0)' : 'none',
  backdropFilter: isFullscreen ? 'none' : 'blur(3px)',
  boxShadow: isFullscreen ? '0 1px 6px rgba(0, 0, 0, 0.45)' : 'none',
  '& svg': {
    color: '#fff',
    fill: 'currentColor',
  },
  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.62)' },

  // In fullscreen the button is fixed to the viewport, below the video top bar.
  ...(isFullscreen &&
    videoTopBarSizes(theme, (barHeight) => ({ top: `calc(${barHeight} + 12px)` }))),

  ...(!isFullscreen && {
    '@media (orientation: landscape) and (max-width: 1199.95px)': {
      top: theme.spacing(0.75),
      right: theme.spacing(0.75),
      minWidth: 36,
      minHeight: 36,
      padding: theme.spacing(0.75),
      '& svg': {
        fontSize: 20,
      },
    },
  }),
}));

export const StyledActionBarBox = styled(Box, {
  shouldForwardProp: (prop) =>
    !['fullscreenVideo', 'showActionBar', 'compactLayout'].includes(prop),
})(({ theme, fullscreenVideo, showActionBar, compactLayout }) => ({
  paddingTop: theme.spacing(compactLayout ? 0.25 : 1),
  paddingRight: `max(${theme.spacing(compactLayout ? 0.5 : 1)}, env(safe-area-inset-right))`,
  paddingBottom: `max(${theme.spacing(compactLayout ? 0.25 : 1)}, env(safe-area-inset-bottom))`,
  paddingLeft: `max(${theme.spacing(compactLayout ? 0.5 : 1)}, env(safe-area-inset-left))`,
  textAlign: 'center',
  backgroundColor: theme.palette.background.common,
  position: fullscreenVideo ? 'fixed' : 'relative',
  bottom: fullscreenVideo !== null ? 0 : 'auto',
  left: fullscreenVideo !== null ? 0 : 'auto',
  width: '100%',
  display: showActionBar ? 'flex' : 'none',
  alignItems: 'center',
  justifyContent: 'space-between',
  zIndex: 1300,
  height: showActionBar ? 'auto' : '0px',
  overflow: 'hidden',
  boxSizing: 'border-box',
  overscrollBehavior: 'contain',
  transition: 'display 0.9s ease-in-out, height 0.9s ease-in-out',
  borderTop: fullscreenVideo ? 'none' : `1px solid ${theme.palette.divider}`,
  boxShadow: fullscreenVideo
    ? 'none'
    : theme.palette.mode === 'dark'
      ? '0 -2px 8px rgba(0, 0, 0, 0.3)'
      : '0 -2px 10px rgba(16, 24, 40, 0.05)',

  '@media (min-width: 1200px) and (max-width: 1599.95px) and (max-height: 1000px)': {
    paddingTop: theme.spacing(compactLayout ? 0.25 : 0.5),
    paddingBottom: `max(${theme.spacing(compactLayout ? 0.25 : 0.5)}, env(safe-area-inset-bottom))`,
  },
}));

export const getStatusTypographyStyle = (status, theme) => ({
  color:
    status === 'down'
      ? theme.palette.error.main
      : status === 'starting'
        ? theme.palette.warning.main
        : status === 'checking'
          ? theme.palette.info.main
          : theme.palette.primary.main,
});

export const StyledTypography = styled(MuiTypography)(({ theme }) => ({
  textAlign: 'left',
  paddingBottom: theme.spacing(2),
  fontSize: '16px',
  paddingLeft: '10px',
}));

export const StyledSupportDivider = styled(Divider)(() => ({
  width: '40%',
  height: '2px',
  backgroundColor: '#D9D9D9',
  '&.MuiDivider-root': {
    margin: '10px 0',
  },
}));

export const StyledDNSInstructionsModal = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 520,
  backgroundColor: theme.palette.background.common,
  boxShadow: 24,
  padding: 32,
  overflow: 'auto',
  maxHeight: 'auto',
  [theme.breakpoints.down('sm')]: {
    width: '80%',
    '@media (orientation: landscape)': {
      maxHeight: '80%',
    },
  },
}));

export const StyledInstructionsModalHeader = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 16,
}));

export const StyledInstructionsButton = styled(Box)(() => ({
  marginTop: 32,
  display: 'flex',
  justifyContent: 'flex-end',
}));

export const StyledStatusTypography = styled(MuiTypography)(() => ({
  fontSize: '13px',
  fontWeight: 700,
  lineHeight: 1.2,
  color: 'white',
  borderRadius: 999,
  padding: '5px 10px',
  display: 'inline-block',
  whiteSpace: 'nowrap',
}));

export const StyledButtonsBox = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: 0,
}));

export const StyledButtonWrapper = styled(Box)(({ theme }) => ({
  margin: theme.spacing(1),
  position: 'relative',
}));

// Header band at the top of the wizard card that holds the stepper.
export const WizardCardHeader = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(-5),
  marginLeft: theme.spacing(-5),
  marginRight: theme.spacing(-5),
  marginBottom: theme.spacing(4),
  padding: theme.spacing(4, 5, 3.5),
  borderBottom: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.down('sm')]: {
    marginTop: theme.spacing(-2.5),
    marginLeft: theme.spacing(-2.5),
    marginRight: theme.spacing(-2.5),
    padding: theme.spacing(2.5, 2.5, 2),
  },
}));

export const StyledStepper = styled(Stepper)(({ theme }) => ({
  padding: 0,
  background: 'transparent',
  maxWidth: 1200,
  marginLeft: 'auto',
  marginRight: 'auto',
  '& .MuiStepLabel-label': {
    marginTop: theme.spacing(1),
    fontSize: 14,
    fontWeight: 400,
    color: theme.palette.text.secondary,
    '&.Mui-active': { color: theme.palette.text.primary, fontWeight: 500 },
    '&.Mui-completed': { color: theme.palette.text.secondary },
  },
}));

// Thick colored progress line between the step nodes.
export const WizardConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 15,
  },
  [`&.${stepConnectorClasses.active} .${stepConnectorClasses.line}, &.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]:
    {
      borderColor: theme.palette.primary.main,
    },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: theme.palette.divider,
    borderTopWidth: 2,
    borderRadius: 2,
    transition: 'border-color 0.3s ease',
  },
}));

export const WizardStepIconRoot = styled('div', {
  shouldForwardProp: (prop) => prop !== 'ownerState',
})(({ theme, ownerState }) => {
  const highlighted = ownerState.active || ownerState.completed;
  return {
    width: 30,
    height: 30,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 500,
    fontSize: 14,
    zIndex: 1,
    backgroundColor: highlighted
      ? theme.palette.primary.main
      : theme.palette.action.disabledBackground,
    color: highlighted ? theme.palette.common.white : theme.palette.text.secondary,
    boxShadow: ownerState.active ? `0 0 0 4px ${alpha(theme.palette.primary.main, 0.16)}` : 'none',
    transition: 'background-color 0.3s ease, box-shadow 0.3s ease',
  };
});

export const StyledWizardCard = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.common,
  borderRadius: 16,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04), 0 10px 30px rgba(0, 0, 0, 0.05)',
  padding: theme.spacing(5),
  minHeight: 700,
  display: 'flex',
  flexDirection: 'column',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2.5),
    borderRadius: 12,
    minHeight: 0,
  },
}));

export const StyledPaper = styled(Paper)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  padding: theme.spacing(4),
  [theme.breakpoints.up(600 + theme.spacing(3) * 2)]: {
    marginBottom: theme.spacing(6),
    padding: theme.spacing(4),
  },
}));

export const StyledLayoutRoot = styled('div')(() => ({
  width: '100%',
  marginLeft: 'auto',
  marginRight: 'auto',
}));

export const StyledDeviceLabelGrid = styled(Grid)(({ theme }) => ({
  marginLeft: '10px',
  marginTop: '10px',
  [theme.breakpoints.down('md')]: {
    overflow: 'auto',
  },
}));

export const StyledFooter = styled('div')(() => ({
  padding: '1rem',
  textAlign: 'center',
  marginTop: 'auto',
}));

export const StyledUserCard = styled(Card)(({ theme }) => ({
  '&.MuiCard-root': {
    background: theme.palette.common.userCard,
    color: theme.palette.common.white,
  },
}));

export const StyledUserCardAvatar = styled(Avatar)(({ theme }) => ({
  '&.MuiAvatar-root': {
    width: '70px',
    height: '70px',
    backgroundColor: theme.palette.primary.main,
  },
}));

export const StyledNotesContainer = styled(Box)(() => ({
  position: 'relative',
  minHeight: 0,
  minWidth: 0,
  width: '100%',
  boxSizing: 'border-box',
}));

export const StyledNotesField = styled(TextField, {
  shouldForwardProp: (prop) => prop !== 'limitReached',
})(({ theme, limitReached }) => ({
  width: '100%',
  minWidth: 0,
  '& .MuiInputBase-root': {
    alignItems: 'stretch',
    paddingBottom: theme.spacing(3),
    boxSizing: 'border-box',
  },
  '& textarea': {
    boxSizing: 'border-box',
    overflow: 'auto !important',
    resize: 'none',
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: limitReached ? theme.palette.warning.main : '',
    },
    '&:hover fieldset': {
      borderColor: limitReached ? theme.palette.warning.main : theme.palette.primary.main,
    },
    '&.Mui-focused fieldset': {
      borderColor: limitReached ? theme.palette.warning.main : theme.palette.primary.main,
    },
  },
  '& .MuiInputLabel-root': {
    color: limitReached ? theme.palette.warning.main : undefined,
    backgroundColor: theme.palette.background.common,
    paddingLeft: theme.spacing(0.5),
    paddingRight: theme.spacing(0.5),
    marginLeft: theme.spacing(-0.5),
    zIndex: 1,
    '&.Mui-focused': {
      color: limitReached ? theme.palette.warning.main : undefined,
    },
  },
}));

export const StyledPageSpinner = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  width: '100%',
}));

// Sidebar dimensions (full vs collapsed icon-rail).
export const SIDEBAR_WIDTH = 224;
export const RAIL_WIDTH = 80;

// Nav bands: tablet [sm, lg) shows the rail, desktop (lg+) the full sidebar.
export const TABLET_MIN = responsiveBreakpoints.sm;
export const DESKTOP_MIN = responsiveBreakpoints.lg;

export const SidebarContainer = styled('aside', {
  shouldForwardProp: (prop) => prop !== 'isopen',
})(({ theme, isopen }) => ({
  position: 'fixed',
  top: 0,
  bottom: 0,
  left: isopen ? 0 : -SIDEBAR_WIDTH,
  zIndex: 5,
  width: SIDEBAR_WIDTH,
  fontWeight: 400,
  color: '#fff',
  backgroundPosition: 'center center',
  backgroundSize: 'cover',
  transition: 'left 0.2s ease-in',

  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: 2,
    width: '100%',
    height: '100%',
    display: 'block',
    background: '#000',
    opacity: 0.33,
  },

  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: 3,
    width: '100%',
    height: '100%',
    background: '#00b398',
    background: 'linear-gradient(to bottom, #00b398, #007864)',
    opacity: 1,
  },

  '&[data-image]::after': {
    opacity: 0.77,
  },

  // Tablet band: slim icon rail, toggled visible/hidden.
  [theme.breakpoints.between('sm', 'lg')]: {
    width: RAIL_WIDTH,
    left: isopen ? 0 : -RAIL_WIDTH,
    '&::before, &::after': {
      opacity: 1,
    },
  },

  // Phone band: full-width overlay drawer, hidden until opened.
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    left: isopen ? 0 : '-100%',
    '&::before, &::after': {
      opacity: 1,
    },
  },
}));

export const SidebarContent = styled('div')(() => ({
  position: 'relative',
  zIndex: 4,
  height: 'calc(100vh - 20px)',
  paddingBottom: 30,
  overflowY: 'auto',
  overflowX: 'hidden',
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  '&::-webkit-scrollbar': {
    width: 0,
  },
  display: 'flex',
  flexDirection: 'column',
}));

export const SidebarBackground = styled('div')(() => ({
  position: 'absolute',
  top: 0,
  left: 0,
  zIndex: 1,
  display: 'block',
  width: '100%',
  height: '100%',
  backgroundPosition: 'center center',
  backgroundSize: 'cover',
  backgroundRepeat: 'no-repeat',
}));

export const StyledNavLink = styled(NavLink, {
  shouldForwardProp: (prop) => prop !== 'rail',
})(({ theme, rail }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(1.25, 2.5),
  color: '#f8f9fa',
  textDecoration: 'none',
  fontSize: '1rem',
  width: '100%',
  borderRadius: 8,
  transition: 'background-color 0.15s ease',
  ...(rail && {
    flexDirection: 'column',
    justifyContent: 'center',
    gap: theme.spacing(0.75),
    padding: theme.spacing(1.5, 0.5),
    textAlign: 'center',
  }),
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '&.active': {
    color: '#fff',
    opacity: 1,
    backgroundColor: theme.palette.action.selected,
  },
}));

export const SidebarNavItemIcon = styled('div', {
  shouldForwardProp: (prop) => prop !== 'rail',
})(() => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
}));

export const SidebarListText = styled(ListItemText, {
  shouldForwardProp: (prop) => prop !== 'rail',
})(({ rail }) => ({
  margin: 0,
  '& .MuiListItemText-primary': {
    textTransform: 'uppercase',
    fontSize: '0.875rem',
    fontWeight: 500,
    letterSpacing: '0.04em',
  },
  ...(rail && {
    width: '100%',
    minWidth: 0,
    textAlign: 'center',
    '& .MuiListItemText-primary': {
      textTransform: 'uppercase',
      fontSize: '0.625rem',
      fontWeight: 600,
      lineHeight: 1.2,
      letterSpacing: '0.02em',
      whiteSpace: 'normal',
      overflowWrap: 'anywhere',
      hyphens: 'auto',
    },
  }),
}));

export const AppWrapper = styled('main')(() => ({
  display: 'flex',
  width: '100%',
  height: '100%',
  padding: 0,
  margin: 0,
  overflowX: 'hidden',
}));

export const EmptyAppWrapper = styled('main')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  padding: 0,
  margin: 0,
  overflowX: 'hidden',
  backgroundColor: theme.palette.grey[100],
}));

export const StyledContent = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isSidebarOpen',
})(({ theme, isSidebarOpen }) => ({
  display: 'inline-flex',
  flexDirection: 'column',
  flexGrow: 1,
  width: '100%',
  minHeight: '100vh',
  padding: 0,
  margin: 0,
  overflowX: 'hidden',
  transition: theme.transitions.create('margin', {
    duration: theme.transitions.duration.standard,
    easing: theme.transitions.easing.easeInOut,
  }),
  marginLeft: isSidebarOpen ? SIDEBAR_WIDTH : 0,

  // Tablet: reserve rail width only while it's visible.
  [theme.breakpoints.between('sm', 'lg')]: {
    marginLeft: isSidebarOpen ? RAIL_WIDTH : 0,
  },

  [theme.breakpoints.down('sm')]: {
    marginLeft: 0,
  },
}));

export const LogoWrapper = styled('span')(({ theme }) => ({
  color: theme.palette.common.white,
  display: 'flex',
  alignItems: 'center',
  marginLeft: '-10px',
  img: {
    margin: '0px 8px 2px 6px',
  },
}));

export const PageWrapper = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(1),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
}));

export const PageSpinner = styled('div')(() => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}));

export const StyledSelect = styled(Select)(({ theme }) => ({
  color: theme.palette.text.info,
  marginRight: '20px',
  fontSize: '12px',
  height: '40px',
  minWidth: '60px',
  '& .MuiSelect-select': {
    padding: '6px 10px',
  },

  [theme.breakpoints.down('sm')]: {
    marginRight: 0,
  },
}));

export const StyledMenuItem = styled(MenuItem)(() => ({
  fontSize: 12,
  display: 'flex',
  alignItems: 'center',
}));

export const StyledFlag = styled('img')(() => ({
  width: 16,
  height: 12,
  marginRight: 4,
}));
